import { Component } from "../lib/component.ts";
import { observeScroll } from "../lib/scroll-observer.ts";

interface DialogueBlockRefs {
  entries?: HTMLDivElement[];
}

class DialogueBlock extends Component<DialogueBlockRefs> {
  #activeEntry: HTMLDivElement | null = null;
  #modeController: AbortController | null = null;
  #imageEntries: HTMLDivElement[] = [];
  #lastPointerX = 0;
  #lastPointerY = 0;
  #hasPointer = false;

  connectedCallback(): void {
    super.connectedCallback();

    if (!this.refs.entries?.length) return;

    this.#imageEntries = this.refs.entries.filter((entry) =>
      entry.querySelector(".dialogue-entry__image"),
    );
    if (!this.#imageEntries.length) return;

    const hoverQuery = window.matchMedia("(hover: hover)");
    hoverQuery.addEventListener("change", () => this.#setupMode(hoverQuery.matches), {
      signal: this.signal,
    });
    this.#setupMode(hoverQuery.matches);
  }

  disconnectedCallback(): void {
    this.#teardownMode();
    this.#imageEntries = [];
    super.disconnectedCallback();
  }

  #setupMode(hasHover: boolean): void {
    this.#teardownMode();
    this.#modeController = new AbortController();
    const modeSignal = this.#modeController.signal;

    this.signal.addEventListener("abort", () => this.#modeController?.abort(), {
      signal: modeSignal,
    });

    const onExit = (): void => this.#setActive(null);

    if (hasHover) {
      window.addEventListener("pointermove", this.#trackPointer, { signal: modeSignal });
      this.addEventListener("pointermove", this.#handlePointerMove, { signal: modeSignal });
      this.addEventListener("pointerleave", this.#handlePointerLeave, { signal: modeSignal });
      observeScroll(this, this.#handlePointerScroll, modeSignal, { onExit });
    } else {
      observeScroll(this, this.#handleTouchScroll, modeSignal, { onExit });
    }
  }

  #teardownMode(): void {
    if (this.#modeController) {
      this.#modeController.abort();
      this.#modeController = null;
    }
    this.#setActive(null);
    this.#hasPointer = false;
  }

  #trackPointer = (event: PointerEvent): void => {
    this.#lastPointerX = event.clientX;
    this.#lastPointerY = event.clientY;
    this.#hasPointer = true;
  };

  #handlePointerMove = (event: PointerEvent): void => {
    this.#activateAtPoint(event.clientX, event.clientY);
  };

  #handlePointerLeave = (): void => {
    this.#setActive(null);
  };

  #handlePointerScroll = (): void => {
    if (!this.#hasPointer) return;
    const rect = this.getBoundingClientRect();
    if (
      this.#lastPointerX < rect.left ||
      this.#lastPointerX > rect.right ||
      this.#lastPointerY < rect.top ||
      this.#lastPointerY > rect.bottom
    ) {
      this.#setActive(null);
      return;
    }
    this.#activateAtPoint(this.#lastPointerX, this.#lastPointerY);
  };

  #activateAtPoint(x: number, y: number): void {
    const elements = document.elementsFromPoint(x, y);
    let match: HTMLDivElement | null = null;

    for (const el of elements) {
      const entry = el.closest(".dialogue-entry");
      if (entry instanceof HTMLDivElement && this.#imageEntries.includes(entry)) {
        match = entry;
        break;
      }
    }

    this.#setActive(match);
  }

  #handleTouchScroll = (): void => {
    const vh = window.innerHeight;
    const zoneTop = vh * 0.35;
    const zoneBottom = vh * 0.65;
    let match: HTMLDivElement | null = null;

    for (const entry of this.#imageEntries) {
      const content = entry.querySelector(".dialogue-entry__content");
      if (!content) continue;

      const rect = content.getBoundingClientRect();
      const center = rect.top + rect.height / 2;

      if (center >= zoneTop && center <= zoneBottom) {
        match = entry;
        break;
      }
    }

    this.#setActive(match);
  };

  #setActive(entry: HTMLDivElement | null): void {
    if (entry === this.#activeEntry) return;

    this.#activeEntry?.removeAttribute("data-active");
    entry?.setAttribute("data-active", "");
    this.#activeEntry = entry;
  }
}

if (!customElements.get("dialogue-block")) {
  customElements.define("dialogue-block", DialogueBlock);
}
